import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

function FeaturedPackages({ packages, loading, onSelectPackage }) {
  const { i18n } = useTranslation();
  const [showPackageDetails, setShowPackageDetails] = useState(null);
  const currentLang = i18n.language || 'bs';

  const resolvePackageImage = (imageValue) => {
    if (!imageValue) return '/img/photos/default.jpg';
    if (imageValue.startsWith('/') || imageValue.startsWith('http')) return imageValue;
    return `/img/photos/${imageValue}`;
  };

  if (loading || !packages || packages.length === 0) return null;

  return (
    <section className="py-32 px-6 md:px-12 bg-white font-sans border-t border-gray-100">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-24">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif text-gray-900 mb-8">Naši paketi</h2>
          <div className="w-16 h-px bg-gray-300 mx-auto mb-8"></div>
          <p className="text-gray-500 text-[10px] tracking-[0.3em] font-bold uppercase max-w-2xl mx-auto">
            Odaberite paket koji najbolje odgovara vašim potrebama
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 md:gap-10">
          {packages.map((pkg) => {
            const packageName = typeof pkg.naziv === 'object' ? pkg.naziv[currentLang] : pkg.naziv;
            const packageDesc = typeof pkg.opis === 'object' ? pkg.opis[currentLang] : pkg.opis;
            const packageDuration = typeof pkg.trajanje === 'object' ? pkg.trajanje[currentLang] : pkg.trajanje;
            
            return (
              <div 
                key={pkg.firestoreId} 
                className="group bg-[#fcfaf8] overflow-hidden border border-gray-100 hover:shadow-2xl transition-all duration-500"
              >
                <div className="relative h-80 overflow-hidden">
                  <img 
                    src={resolvePackageImage(pkg.slika)}
                    alt={packageName}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-1000"
                  />
                  <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                </div>

                <div className="p-8 space-y-6">
                  <div className="flex justify-between items-end">
                    <div className="space-y-1">
                      <span className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em]">{packageDuration}</span>
                      <h3 className="text-2xl font-serif text-gray-900 leading-tight italic">{packageName}</h3>
                    </div>
                    <span className="text-2xl font-light text-gray-900">{pkg.cijena} {pkg.valuta}</span>
                  </div>

                  {packageDesc && (
                    <p className="text-gray-500 text-sm font-light leading-relaxed line-clamp-3 italic">
                      {packageDesc}
                    </p>
                  )}

                  <div className="flex gap-4 pt-4 border-t border-gray-100">
                    <button 
                      onClick={() => setShowPackageDetails(pkg)}
                      className="flex-1 text-[10px] font-bold text-gray-400 tracking-[0.2em] uppercase hover:text-gray-900 transition-colors py-2"
                    >
                      {currentLang === 'bs' ? 'Detalji' : 'Details'}
                    </button>
                    <button 
                      onClick={() => onSelectPackage(pkg)}
                      className="flex-[1.5] text-[10px] font-bold text-gray-900 tracking-[0.2em] uppercase hover:opacity-50 transition-all py-2 border-b border-gray-900 flex items-center justify-center gap-2"
                    >
                      {currentLang === 'bs' ? 'ZATRAŽI PONUDU' : 'REQUEST QUOTE'} &rarr;
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* PACKAGE DETAILS MODAL */}
      {showPackageDetails && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
          <div 
            className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm"
            onClick={() => setShowPackageDetails(null)}
          ></div>
          <div className="relative bg-white w-full max-w-2xl rounded-sm shadow-2xl overflow-hidden animate-fade-in-up font-sans">
            <button 
              onClick={() => setShowPackageDetails(null)}
              className="absolute top-6 right-6 z-10 w-10 h-10 hover:opacity-50 transition-all flex items-center justify-center"
            >
              <svg className="w-6 h-6 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="h-64 sm:h-80 relative overflow-hidden">
              <img 
                src={resolvePackageImage(showPackageDetails.slika)}
                alt={typeof showPackageDetails.naziv === 'object' ? showPackageDetails.naziv[currentLang] : showPackageDetails.naziv}
                className="w-full h-full object-cover grayscale"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              <div className="absolute bottom-10 left-10">
                <h3 className="text-4xl font-serif text-white italic">
                  {typeof showPackageDetails.naziv === 'object' ? showPackageDetails.naziv[currentLang] : showPackageDetails.naziv}
                </h3>
              </div>
            </div>

            <div className="p-10 space-y-10 max-h-[50vh] overflow-y-auto">
              {showPackageDetails.opis && (
                <div className="space-y-4">
                  <h4 className="text-[10px] font-bold text-gray-400 tracking-[0.3em] uppercase">Opis paketa</h4>
                  <p className="text-gray-600 leading-relaxed italic text-lg font-serif">
                    {typeof showPackageDetails.opis === 'object' ? (showPackageDetails.opis[currentLang] || showPackageDetails.opis.bs) : showPackageDetails.opis}
                  </p>
                </div>
              )}

              {showPackageDetails.usluge && showPackageDetails.usluge.length > 0 && (
                <div className="space-y-6">
                  <h4 className="text-[10px] font-bold text-gray-400 tracking-[0.3em] uppercase">Uključene usluge</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-12">
                    {showPackageDetails.usluge.map((service, idx) => {
                      const serviceName = typeof service === 'object' ? (service[currentLang] || service.bs) : service;
                      return (
                        <div key={idx} className="flex items-center gap-4 border-b border-gray-50 pb-3">
                          <div className="w-1 h-1 bg-gray-300"></div>
                          <span className="text-xs font-medium text-gray-700 tracking-wide uppercase">{serviceName}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="p-10 bg-gray-50 flex flex-col sm:flex-row justify-between items-center gap-6">
              <div className="text-center sm:text-left">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Cijena paketa</p>
                <span className="text-3xl font-light text-gray-900">{showPackageDetails.cijena} {showPackageDetails.valuta}</span>
              </div>
              <button 
                onClick={() => {
                  onSelectPackage(showPackageDetails);
                  setShowPackageDetails(null);
                }}
                className="text-gray-900 px-12 py-4 font-bold tracking-[0.3em] uppercase hover:opacity-50 transition-all border-b border-gray-900 text-xs"
              >
                {currentLang === 'bs' ? 'Zatraži ponudu' : 'Request quote'} &rarr;
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default FeaturedPackages;
